/**
 * Auth-token handoff bootstrap helper.
 *
 * After a successful Devise sign-up or login (email or OAuth) Rails
 * redirects to `/?t=<jwt>&auth_event=…&auth_method=…`. The JWT must reach
 * localStorage, but it must never be observable by gtm.js: any tag that
 * reads the page URL (an automatic page_view, a Custom HTML tag) would send
 * the token to a third party. GTM is bootstrapped by the `consent.ts`
 * entrypoint, which runs before React, so the strip has to happen there
 * rather than in a React effect.
 *
 * `captureAuthTokenHandoff()` runs synchronously in `consent.ts` before
 * `bootstrapGtm()`, and again from AuthContext as an idempotent fallback. It
 * reads `location.search` only (never `new URL(location.href)`), so it also
 * works where tests stub `window.location` with a plain object.
 *
 * `auth_event` / `auth_method` are left in place: they carry no secret and
 * `captureAuthEventSentinel()` consumes them at App.tsx module load.
 */

const TOKEN_PARAM = "t";
const STORAGE_KEY = "auth_token";

/**
 * Move `t` from the URL into localStorage and rewrite the URL without it,
 * keeping every other param and the hash. A no-op when `t` is absent, so it
 * is safe to call more than once.
 */
export function captureAuthTokenHandoff(): void {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  if (!params.has(TOKEN_PARAM)) return;

  const token = params.get(TOKEN_PARAM);
  if (token) {
    try {
      window.localStorage.setItem(STORAGE_KEY, token);
    } catch {
      // localStorage may be unavailable (private mode, quota); the URL is
      // still scrubbed below so the token never reaches a tag
    }
  }

  params.delete(TOKEN_PARAM);
  const search = params.toString();
  const url = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`;
  window.history.replaceState({}, document.title, url);
}
